import { TipoContatoService } from './TipoContatoService';
import { apiFetch } from './api';
import { etapasDoMiolo, nomeEtapa } from '../data/etapas';

const API_URL = import.meta.env.VITE_API_URL || '';
const BASE = `${API_URL}/api/relatoriopersonalizado`;

/* ─── Catálogo ────────────────────────────────────────────────── */
/* Os tipos abaixo foram medidos contra a API rodando (18/09/2026), não
   deduzidos do documento: o contrato real difere dele em alguns pontos
   (filtro é {dimensao, operador, valores} com valores em texto; `dimensoes`
   é lista de strings; não há campo de versão no request). */

export interface CatalogoItem {
    id: string;
    nome: string;
    /** Tipo do VALOR da medida — `inteiro`, `decimal`, `percentual`. Vem `null` nas datas. */
    tipo: string | null;
    /** Só nas medidas: como o número é obtido, em português, para a tela explicar. */
    definicao?: string | null;
}

export interface CatalogoFonte {
    id: string;
    nome: string;
    medidas: CatalogoItem[];
    dimensoes: CatalogoItem[];
    datas: CatalogoItem[];
}

export interface CatalogoLimites {
    maximoDimensoes: number;
    maximoMedidas: number;
    maximoFiltros: number;
    maximoLinhas: number;
    maximoMesesPeriodo: number;
    /** Blocos por painel. Cada bloco é uma consulta — doze já são doze chamadas ao abrir. */
    maximoBlocos: number;
}

export interface Catalogo {
    fontes: CatalogoFonte[];
    limites: CatalogoLimites;
    presets: CatalogoItem[];
}

/* ─── Especificação ───────────────────────────────────────────── */

export type NivelRelatorio = 'agregado' | 'registros';
export type VisualRelatorio = 'numero' | 'tabela' | 'barras' | 'linha' | 'pizza' | 'lista';

export interface FiltroEspecificacao {
    dimensao: string;
    operador: string;
    /** Sempre texto, inclusive para id numérico. `"null"` filtra quem não tem valor. */
    valores: string[];
}

export interface PeriodoEspecificacao {
    /** Id de uma das datas da fonte: `criado_em`, `fechado_em`, `data`, `mes`. */
    data: string;
    preset: string;
    /** Só em `personalizado`. Sem fuso — o servidor trata como dia civil em UTC. */
    de?: string | null;
    ate?: string | null;
}

export interface EspecificacaoRelatorio {
    fonte: string;
    nivel: NivelRelatorio;
    medidas: { id: string }[];
    dimensoes: string[];
    filtros: FiltroEspecificacao[];
    periodo: PeriodoEspecificacao;
    visual: VisualRelatorio;
}

/* ─── Painel ──────────────────────────────────────────────────────
   Um relatório é um painel de blocos, e cada bloco é uma pergunta com o seu
   formato e o seu lugar na grade de 12 colunas. O período é UM, do painel.  */

export interface BlocoPainel {
    /** Único dentro do painel; é a chave de render e de resultado. */
    id: string;
    titulo: string | null;
    /** Coluna inicial (0–11) e linha inicial, ambas em células da grade. */
    x: number;
    y: number;
    /** Largura em colunas (mínimo 3) e altura em linhas de 88px (mínimo 1). */
    w: number;
    h: number;
    consulta: EspecificacaoRelatorio;
}

/** O bloco reduzido ao que a miniatura precisa: lugar, tamanho e formato. */
export interface ArranjoBloco {
    x: number;
    y: number;
    w: number;
    h: number;
    visual: VisualRelatorio;
    titulo: string | null;
}

export interface PainelRelatorio {
    versao: number;
    periodo: PeriodoEspecificacao;
    blocos: BlocoPainel[];
    /**
     * Dimensões que o painel oferece como filtro **no topo**, ao lado do período
     * (hoje: `vendedor`). A escolha é uma lente de quem olha — não muda o relatório
     * salvo, igual ao período. Painel sem isto se comporta como sempre.
     */
    filtrosDoPainel?: string[];
}

/**
 * A especificação com que um bloco é consultado: o **intervalo** vem do painel,
 * a **data** vem do bloco.
 *
 * Não é preciosismo — é o que permite um painel misturar fontes. Negócios conta
 * por `criado_em`, Ligações por `data`, Metas por `mes`; se a data descesse do
 * topo, o painel "Desempenho da equipe" seria recusado pela API com "a fonte
 * Ligações não tem a data criado_em". O servidor aplica a mesma regra ao salvar.
 */
export const consultaDoBloco = (
    bloco: BlocoPainel,
    periodo: PeriodoEspecificacao,
    /** O filtro do topo, já resolvido: dimensão → valores. Vazio = sem filtro. */
    filtrosDoTopo: FiltroEspecificacao[] = [],
    /** Dimensões que a fonte do bloco conhece; sem isto nenhum filtro do topo é aplicado. */
    dimensoesDaFonte: string[] = [],
): EspecificacaoRelatorio => {
    /* O filtro do topo só entra onde a fonte o entende — num painel misto, "responsável"
       vale para Negócios e Contatos, e um bloco de Metas simplesmente não é filtrado.
       Ele VENCE o filtro de mesma dimensão gravado no bloco: quem está olhando acabou de
       escolher, e dois filtros da mesma coisa dariam interseção vazia sem explicação. */
    const aplicaveis = filtrosDoTopo.filter(f => f.valores.length > 0 && dimensoesDaFonte.includes(f.dimensao));
    const doBloco = bloco.consulta.filtros.filter(f => !aplicaveis.some(x => x.dimensao === f.dimensao));

    return {
        ...bloco.consulta,
        filtros: [...doBloco, ...aplicaveis],
        periodo: { ...periodo, data: bloco.consulta.periodo?.data || periodo.data },
    };
};

/* ─── Resultado ───────────────────────────────────────────────── */

export interface ColunaResultado {
    id: string;
    nome: string;
    /**
     * `texto`, `tempo`, `inteiro`, `decimal`, `percentual` ou `data`. Decide o
     * alinhamento, o formato do valor e quais gráficos servem — `tempo` na primeira
     * coluna é o que habilita o gráfico de linha.
     */
    tipo: string;
}

export interface ResultadoRelatorio {
    colunas: ColunaResultado[];
    /** Matriz posicional, alinhada com `colunas`. */
    linhas: (string | number | null)[][];
    totais: (string | number | null)[];
    /** Frase de fonte: o que foi consultado, em que período e com que escopo. */
    rotulo: string;
    avisos: string[];
    truncado: boolean;
}

/* ─── Relatório salvo ─────────────────────────────────────────── */

export interface RelatorioSalvo {
    /** Negativo = pronto da plataforma, montado no servidor e não editável. */
    relatorioSalvoId: number;
    nome: string;
    descricao: string | null;
    fonte: string;
    visibilidade: 'pessoal' | 'empresa';
    sistema: boolean;
    usuarioCriadorId: number;
    podeEditar: boolean;
    atualizadoEm: string;
    /** Quantos blocos o painel tem — vem também na listagem, sem carregar o painel. */
    totalBlocos: number;
    /**
     * A planta do painel — onde cada bloco fica e de que formato é, sem a consulta.
     * Vem sempre, inclusive na listagem, e é o que a galeria em blocos desenha como
     * miniatura. Prévia com DADO custaria uma consulta por relatório (medido: 21
     * chamadas e ~700ms para dez), e cresce com o tamanho da galeria.
     */
    arranjo: ArranjoBloco[];
    /** Formato do primeiro bloco; a biblioteca de modelos rotula a pergunta com ele. */
    visual: VisualRelatorio;
    /**
     * Só vem em BuscarPorId, Criar e Duplicar — a listagem é enxuta de propósito.
     * Relatório salvo antes do painel chega convertido em painel de um bloco: a
     * conversão é na leitura, no servidor, e aqui só existe um formato.
     */
    painel: PainelRelatorio | null;
}

export interface SalvarRelatorioPayload {
    nome: string;
    descricao?: string | null;
    visibilidade: 'pessoal' | 'empresa';
    painel: PainelRelatorio;
}

/* ─── Transporte ──────────────────────────────────────────────── */

/**
 * Lê a resposta sem `res.json()` cru: rota inexistente devolve 404 com corpo
 * VAZIO e o parser estoura antes de qualquer checagem, entregando ao usuário
 * uma mensagem de parser no lugar do problema real (já aconteceu com o
 * assistente). Aqui o corpo vem como texto e a mensagem é em português.
 */
async function ler<T>(res: Response): Promise<T> {
    const texto = await res.text();

    if (!texto) {
        if (res.status === 404) throw new Error('A API não tem este recurso. Reinicie a API local se ela for anterior aos relatórios.');
        if (res.status === 401) throw new Error('Sua sessão expirou. Entre de novo.');
        throw new Error(`A API respondeu ${res.status} sem conteúdo.`);
    }

    let corpo: { sucesso?: boolean; mensagem?: string; dados?: T };
    try {
        corpo = JSON.parse(texto);
    } catch {
        throw new Error('A API respondeu em um formato inesperado.');
    }

    if (!corpo.sucesso) throw new Error(corpo.mensagem || 'Não foi possível concluir a operação.');
    return corpo.dados as T;
}

const RelatorioPersonalizadoService = {
    getCatalogo: async (): Promise<Catalogo> =>
        ler<Catalogo>(await apiFetch(`${BASE}/Catalogo`)),

    consultar: async (especificacao: EspecificacaoRelatorio): Promise<ResultadoRelatorio> =>
        ler<ResultadoRelatorio>(await apiFetch(`${BASE}/Consultar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(especificacao),
        })),

    listar: async (): Promise<RelatorioSalvo[]> =>
        ler<RelatorioSalvo[]>(await apiFetch(`${BASE}/BuscarTodos`)),

    buscar: async (id: number): Promise<RelatorioSalvo> =>
        ler<RelatorioSalvo>(await apiFetch(`${BASE}/BuscarPorId/${id}`)),

    criar: async (payload: SalvarRelatorioPayload): Promise<RelatorioSalvo> =>
        ler<RelatorioSalvo>(await apiFetch(`${BASE}/Criar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })),

    atualizar: async (id: number, payload: SalvarRelatorioPayload): Promise<RelatorioSalvo> =>
        ler<RelatorioSalvo>(await apiFetch(`${BASE}/Atualizar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ relatorioSalvoId: id, ...payload }),
        })),

    remover: async (id: number): Promise<void> => {
        await ler<unknown>(await apiFetch(`${BASE}/Remover/${id}`, { method: 'DELETE' }));
    },

    duplicar: async (id: number): Promise<RelatorioSalvo> =>
        ler<RelatorioSalvo>(await apiFetch(`${BASE}/Duplicar/${id}`, { method: 'POST' })),
};

export default RelatorioPersonalizadoService;

/* ─── Formatação, compartilhada pelas três telas ──────────────── */

export const formatarValor = (valor: string | number | null, tipo: string): string => {
    if (valor === null || valor === undefined) return '—';

    if (tipo === 'percentual') {
        return `${(Number(valor) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
    }
    if (tipo === 'decimal') {
        return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
    if (tipo === 'inteiro') {
        return Number(valor).toLocaleString('pt-BR');
    }
    if (tipo === 'data') {
        const d = new Date(String(valor));
        return Number.isNaN(d.getTime()) ? String(valor) : d.toLocaleDateString('pt-BR');
    }
    return String(valor);
};

/** Número à direita, texto à esquerda — a régua que deixa a coluna comparável. */
export const ehNumerica = (tipo: string): boolean =>
    tipo === 'inteiro' || tipo === 'decimal' || tipo === 'percentual';

/* ─── Valores possíveis de cada dimensão ──────────────────────────
   O catálogo diz QUAIS dimensões existem, mas não quais VALORES elas têm —
   e o filtro exige o id, não o rótulo. Sem isso o usuário teria que digitar
   "6" para dizer "Venda perdida". Enquanto a API não expõe os valores, eles
   saem de onde o app já os tem: enum fixo quando o domínio é fechado, e o
   cadastro da empresa quando é aberto.
   Pendência registrada: um `Catalogo/Valores?dimensao=` no servidor resolveria
   isto sem o front conhecer cinco endpoints.                                */

export interface ValorDimensao { id: string; nome: string }

/* As etapas do funil da empresa, com o nome dela: as do robô, o miolo (inclusive as que ela
   criou) e os dois desfechos. Montado na hora porque o funil chega depois do login. */
const situacoes = (): ValorDimensao[] =>
    [1, 8, ...etapasDoMiolo(), 3, 6].map(id => ({ id: String(id), nome: nomeEtapa(id) }));

const ORIGENS: ValorDimensao[] = [
    { id: '1', nome: 'WhatsApp (Meta)' },
    { id: '2', nome: 'WhatsApp (Evolution)' },
    { id: '3', nome: 'Instagram' },
    { id: '4', nome: 'Messenger' },
    { id: '5', nome: 'Lead Ads' },
    { id: '6', nome: 'Manual' },
];

/* Os três tipos que o banco grava, em INGLÊS — é assim que a tela de metas
   cadastra. Vale dizer porque já custou caro: o `MetaService` comparava com
   "valor", em português, e toda meta de valor caía no ramo errado, devolvendo a
   contagem de vendas no lugar da soma (corrigido por outra sessão em 18/09). */
const TIPOS_META: ValorDimensao[] = [
    { id: 'value', nome: 'Faturamento' },
    { id: 'quantity', nome: 'Quantidade de vendas' },
    { id: 'calls', nome: 'Ligações' },
];

/* Os dois estados do funil. A chave é 0/1, a mesma que a API devolve como rótulo:
   o filtro salvo não depende da frase da tela. */
const SITUACOES_FUNIL: ValorDimensao[] = [
    { id: '0', nome: 'No funil' },
    { id: '1', nome: 'Fora do funil' },
];

/** `null` = a dimensão não tem lista conhecida; a tela esconde o filtro dela. */
export async function carregarValores(dimensaoId: string): Promise<ValorDimensao[] | null> {
    if (dimensaoId === 'situacao') return situacoes();
    if (dimensaoId === 'origem') return ORIGENS;
    if (dimensaoId === 'tipo_meta') return TIPOS_META;
    if (dimensaoId === 'funil') return SITUACOES_FUNIL;

    try {
        if (dimensaoId === 'vendedor' || dimensaoId === 'usuario' || dimensaoId === 'alvo') {
            const res = await apiFetch(`${API_URL}/api/Usuario/BuscarTodos`);
            const dados = await ler<{ userId: number; userName: string }[]>(res);
            return dados.map(u => ({ id: String(u.userId), nome: u.userName }));
        }

        if (dimensaoId === 'campanha') {
            const res = await apiFetch(`${API_URL}/api/campanha/BuscarTodos`);
            const dados = await ler<{ campanhaId: number; campanhaNome: string }[]>(res);
            return dados.map(c => ({ id: String(c.campanhaId), nome: c.campanhaNome }));
        }

        /* O nome do tipo é editável pela empresa, então o filtro casa por ID — mesmo
           motivo do filtro de tipo no Chat e na Pipeline. */
        if (dimensaoId === 'tipo_contato') {
            const resposta = await TipoContatoService.getAll();
            return resposta.dados.map(t => ({ id: String(t.tipoContatoId), nome: t.nome }));
        }

        if (dimensaoId === 'motivo_perda') {
            const res = await apiFetch(`${API_URL}/api/MotivoPerda/BuscarTodos`);
            const dados = await ler<{ motivoPerdaId: number; descricao: string }[]>(res);
            return dados.map(m => ({ id: String(m.motivoPerdaId), nome: m.descricao }));
        }
    } catch {
        // Lista indisponível não impede montar o relatório — só esconde o filtro.
        return null;
    }

    return null;
}
